import React, { useState, useEffect, useRef } from 'react';
import {
  ChatBubbleLeftEllipsisIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import {
  getClientConversation,
  markConversationMessagesRead,
  sendClientConversationMessage,
} from '../../api/backend';
import './ClientMessages.css';

const formatMessageTimestamp = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : '—';
};

const ClientMessages = () => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [messages, setMessages] = useState([]);
  const chatHistoryRef = useRef(null);

  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    let cancelled = false;

    const loadConversation = async () => {
      try {
        const response = await getClientConversation();
        if (cancelled) return;
        setMessages(response.messages || []);
      } catch (err) {
        console.error('❌ Error al cargar mensajes del cliente:', err);
        setStatus('error');
      }
    };

    const initConversation = async () => {
      try {
        await markConversationMessagesRead();
      } catch (error) {
        console.error('❌ Error marcando mensajes como leídos:', error);
      }
      await loadConversation();
    };

    initConversation();
    const intervalId = window.setInterval(loadConversation, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);


  const sendMessage = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await sendClientConversationMessage({
        subject,
        body,
        attachments: attachments || [],
      });

      setSubject('');
      setBody('');
      setAttachments([]);
      setStatus('sent');
      const response = await getClientConversation();
      setMessages(response.messages || []);
  
    } catch (err) {
      console.error('❌ Error al enviar o guardar mensaje:', err);
      setStatus('error');
    }
  };
  
  

  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    const promises = files.map(file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve({
          content: reader.result.split(',')[1],
          filename: file.name,
          type: file.type,
        });
      };
      reader.readAsDataURL(file);
    }));
    Promise.all(promises).then(setAttachments);
  };

  return (
    <div className="messaging-console">
      <div className="messaging-header">
        <div className="messaging-header__identity">
          <div className="messaging-header__avatar">
            <ChatBubbleLeftEllipsisIcon className="h-6 w-6" />
          </div>
          <div>
            <p className="messaging-header__eyebrow">Support Desk</p>
            <h3>Mensajería con el administrador</h3>
          </div>
        </div>
        <div className="messaging-header__status">
          <span className="messaging-header__dot" />
          Canal activo
        </div>
      </div>

      <div className="chat-box">
        <div className="chat-history" ref={chatHistoryRef}>
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400 py-16">
              <ChatBubbleLeftEllipsisIcon className="h-12 w-12 opacity-25" />
              <p className="text-sm">Aún no hay mensajes. Envía el primero.</p>
            </div>
          )}
          {messages.map(msg => (
            <div key={msg.id} className={`message ${msg.from === 'client' ? 'sent' : 'received'}`}>
              <div className="message-meta">
                <strong>{msg.from === 'client' ? 'Tu' : 'Administrador'}</strong>
                <span>{formatMessageTimestamp(msg.timestamp)}</span>
              </div>
              {msg.subject ? <div className="message-subject">{msg.subject}</div> : null}
              <div className="message-body">{msg.body}</div>
              {msg.attachments?.map((a, i) => (
                <div key={i} className="attachment-chip">
                  <a href={`data:${a.type};base64,${a.content}`} download={a.filename}>
                    {a.filename}
                  </a>
                </div>
              ))}
            </div>
          ))}
        </div>
  
        <form className="chat-input" onSubmit={sendMessage}>
          <div className="chat-input__grid">
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Asunto"
              required
            />

            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && subject && body && status !== 'sending') {
                  sendMessage(e);
                }
              }}
              placeholder="Escribe tu mensaje… (Ctrl+Enter para enviar)"
              required
            />

            <div className="chat-input__actions">
              <input type="file" multiple onChange={handleFiles} />

              <button
                type="submit"
                disabled={!subject || !body || status === 'sending'}
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {status === 'sending' ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </div>

          {attachments.length > 0 && (
            <div className="attachments-preview">
              {attachments.map((file, index) => (
                <span key={`${file.filename}-${index}`} className="attachment-chip attachment-chip--draft">
                  {file.filename}
                </span>
              ))}
            </div>
          )}

          {status === 'sent' && <p className="message-feedback success">Mensaje enviado ✅</p>}
          {status === 'error' && <p className="message-feedback error">Error al enviar mensaje ❌</p>}
        </form>
      </div>
    </div>

  );
  
};

export default ClientMessages;
